import { useState, useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Search, CheckCircle2, AlertCircle, Sparkles, Loader2, Mail, Globe, Lock } from "lucide-react";
import { toast } from "sonner";
import { API_BASE } from "../lib/api";

/**
 * Public SEO/AEO Audit Widget.
 * Route: /tools/audit/:agencyId?rep=<userId>
 *
 * Prospects land here (via iframe embed on partner sites OR direct link), enter
 * their website URL, and get an instant Claude-generated audit teaser. To unlock
 * the full report (with all 5 strengths, opportunities, and PDF download) they
 * provide their email — which fires the PDF to their inbox AND notifies the
 * attributed rep in-app + via email so follow-up happens while interest is hot.
 * No auth required.
 */
export default function PublicAuditWidgetPage() {
  const { agencyId } = useParams();
  const [params] = useSearchParams();
  const repId = params.get("rep") || null;

  const [agency, setAgency] = useState(null);
  const [websiteUrl, setWebsiteUrl] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [running, setRunning] = useState(false);
  const [teaser, setTeaser] = useState(null);

  // Email opt-in state
  const [email, setEmail] = useState("");
  const [prospectName, setProspectName] = useState("");
  const [prospectCompany, setProspectCompany] = useState("");
  const [emailing, setEmailing] = useState(false);
  const [emailed, setEmailed] = useState(false);

  useEffect(() => {
    if (!agencyId) return;
    axios.get(`${API_BASE}/api/public/agencies/${agencyId}`)
      .then(r => setAgency(r.data))
      .catch(() => setAgency({ name: "Ready Prospect", logo_url: null }));
  }, [agencyId]);

  const runAudit = async (e) => {
    e?.preventDefault();
    let url = websiteUrl.trim();
    if (!url) {
      toast.error("Please enter a website URL");
      return;
    }
    if (!/^https?:\/\//i.test(url)) url = "https://" + url;
    setRunning(true);
    setTeaser(null);
    setEmailed(false);
    try {
      const r = await axios.post(`${API_BASE}/api/public/audits/preview`, {
        website_url: url,
        business_name: businessName.trim() || null,
        agency_id: agencyId,
        rep_id: repId,
      });
      setTeaser(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not audit that site — please check the URL.");
    } finally {
      setRunning(false);
    }
  };

  const submitEmail = async () => {
    if (!email.trim() || !/.+@.+\..+/.test(email.trim())) {
      toast.error("Please enter a valid email");
      return;
    }
    setEmailing(true);
    try {
      const r = await axios.post(`${API_BASE}/api/public/audits/${teaser.audit_id}/email-report`, {
        email: email.trim(),
        name: prospectName.trim() || null,
        company: prospectCompany.trim() || null,
      });
      if (r.data?.email_sent === false) {
        toast.warning(
          "Request received. If you don't see the report in your inbox in a few minutes, please contact us directly.",
          { duration: 10000 },
        );
      } else {
        toast.success(`Full audit sent to ${email.trim()}`);
      }
      setEmailed(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Send failed — please try again.");
    } finally {
      setEmailing(false);
    }
  };

  const gradeBg = (grade) => {
    const g = (grade || "").toUpperCase().charAt(0);
    if (g === "A" || g === "B") return "bg-[#0F2747]";
    return "bg-[#B91C1C]";
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0F2747] via-[#162B49] to-[#081A33]">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10">
        {/* Co-branded header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <div className="flex items-center gap-2 text-[#FCA5A5] text-xs font-bold tracking-widest uppercase">
              <Sparkles className="h-4 w-4" /> Instant SEO + AEO Audit
            </div>
            <h1 className="font-outfit text-3xl sm:text-4xl font-bold text-white mt-2">
              How does your site score on Google + AI answer engines?
            </h1>
            <p className="text-zinc-300 mt-2 text-sm max-w-2xl">
              Enter your URL. In under a minute you&apos;ll see two letter grades, the top strengths and
              opportunities, and three quick wins to move up a grade.
            </p>
          </div>
          {agency?.logo_url && (
            <img
              src={`${API_BASE}${agency.logo_url}`}
              alt={agency.name}
              className="h-14 max-w-[180px] object-contain bg-white rounded-lg p-2"
              onError={(e) => { e.target.style.display = "none"; }}
            />
          )}
        </div>

        {/* URL input card */}
        <Card className="border-0 shadow-2xl mb-6" data-testid="audit-widget-input-card">
          <CardContent className="pt-6">
            <form onSubmit={runAudit} className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <Label htmlFor="website" className="flex items-center gap-1 text-slate-700 font-semibold">
                    <Globe className="h-4 w-4" /> Website URL <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="website"
                    data-testid="audit-widget-url-input"
                    type="text"
                    placeholder="https://yourbusiness.com"
                    value={websiteUrl}
                    onChange={(e) => setWebsiteUrl(e.target.value)}
                    disabled={running}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="business" className="text-slate-700 font-semibold">Business Name (optional)</Label>
                  <Input
                    id="business"
                    data-testid="audit-widget-business-input"
                    type="text"
                    placeholder="Acme Roofing"
                    value={businessName}
                    onChange={(e) => setBusinessName(e.target.value)}
                    disabled={running}
                    className="mt-1"
                  />
                </div>
              </div>
              <Button
                type="submit"
                disabled={running}
                data-testid="audit-widget-run-btn"
                className="w-full bg-[#0F2747] hover:bg-[#081A33] text-white h-12 text-base font-semibold"
              >
                {running ? (
                  <><Loader2 className="h-5 w-5 mr-2 animate-spin" /> Analyzing your site — this takes ~30–60 seconds…</>
                ) : (
                  <><Search className="h-5 w-5 mr-2" /> Run My Free Audit</>
                )}
              </Button>
              <p className="text-xs text-slate-500 text-center">
                We scan your homepage + up to 5 internal pages. No signup required to see your grades.
              </p>
            </form>
          </CardContent>
        </Card>

        {/* Teaser results */}
        {teaser && (
          <div className="space-y-4" data-testid="audit-widget-results">
            {/* Report card */}
            <Card className="border-0 shadow-2xl overflow-hidden">
              <div className="bg-[#0F2747] px-6 py-4">
                <div className="text-[#FCA5A5] text-xs font-bold tracking-widest uppercase">Your Report Card</div>
                <div className="text-white text-xl font-bold mt-1">{teaser.client_name}</div>
                <div className="text-slate-300 text-sm">{teaser.website_url}</div>
              </div>
              <CardContent className="pt-6">
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="border-l-4 border-[#0F2747] bg-slate-50 p-4 rounded-r-lg" data-testid="seo-grade-card">
                    <div className="flex items-center gap-4">
                      <div className={`${gradeBg(teaser.seo_grade)} w-24 h-24 rounded-lg flex items-center justify-center text-white font-bold text-5xl font-serif`}>
                        {teaser.seo_grade}
                      </div>
                      <div className="flex-1">
                        <div className="text-xl font-bold text-slate-900">SEO Grade</div>
                        <div className="text-xs text-slate-500">Search Engine Optimization</div>
                        <div className={`${gradeBg(teaser.seo_grade)} inline-block text-white text-xs font-bold rounded px-2 py-0.5 mt-2`}>
                          {teaser.seo_score} / 100
                        </div>
                      </div>
                    </div>
                    <p className="text-sm text-slate-700 mt-3">{teaser.seo_summary}</p>
                  </div>
                  <div className="border-l-4 border-[#B91C1C] bg-slate-50 p-4 rounded-r-lg" data-testid="aeo-grade-card">
                    <div className="flex items-center gap-4">
                      <div className={`${gradeBg(teaser.aeo_grade)} w-24 h-24 rounded-lg flex items-center justify-center text-white font-bold text-5xl font-serif`}>
                        {teaser.aeo_grade}
                      </div>
                      <div className="flex-1">
                        <div className="text-xl font-bold text-slate-900">AEO Grade</div>
                        <div className="text-xs text-slate-500">Answer Engine Optimization</div>
                        <div className={`${gradeBg(teaser.aeo_grade)} inline-block text-white text-xs font-bold rounded px-2 py-0.5 mt-2`}>
                          {teaser.aeo_score} / 100
                        </div>
                      </div>
                    </div>
                    <p className="text-sm text-slate-700 mt-3">{teaser.aeo_summary}</p>
                  </div>
                </div>

                {/* Recommendation */}
                <div className="mt-6 bg-[#0F2747] text-white p-4 rounded-lg" data-testid="audit-recommendation">
                  <div className="text-[#FCA5A5] text-xs font-bold tracking-widest uppercase mb-2">Our Recommendation</div>
                  <p className="text-white">{teaser.recommendation}</p>
                </div>

                {/* Top 3 Quick Wins */}
                <div className="mt-6">
                  <div className="text-slate-900 font-bold mb-3">Top 3 Quick Wins to Move Up a Grade</div>
                  <div className="grid sm:grid-cols-3 gap-3">
                    {teaser.quick_wins?.map((qw, i) => (
                      <div key={i} className="bg-slate-50 border border-slate-200 rounded-lg p-3" data-testid={`quick-win-${i}`}>
                        <div className="text-[#B91C1C] font-serif text-3xl font-bold">{i + 1}</div>
                        <div className="font-semibold text-slate-900 text-sm">{qw.title}</div>
                        <div className="text-xs text-slate-600 mt-1">{qw.detail}</div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Preview of strengths + opportunities */}
                <div className="grid sm:grid-cols-2 gap-4 mt-6">
                  <div>
                    <div className="text-emerald-700 font-bold text-sm mb-2 flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4" /> What&apos;s Working ({teaser.strengths_total} total)
                    </div>
                    {teaser.strengths_preview?.map((s, i) => (
                      <div key={i} className="border-l-2 border-emerald-500 pl-3 py-2 mb-2" data-testid={`strength-preview-${i}`}>
                        <div className="font-semibold text-slate-900 text-sm">{s.title}</div>
                        <div className="text-xs text-slate-600">{s.detail}</div>
                      </div>
                    ))}
                    <div className="text-xs text-slate-500 italic mt-1 flex items-center gap-1">
                      <Lock className="h-3 w-3" /> {teaser.strengths_total - (teaser.strengths_preview?.length || 0)} more in the full report
                    </div>
                  </div>
                  <div>
                    <div className="text-amber-700 font-bold text-sm mb-2 flex items-center gap-2">
                      <AlertCircle className="h-4 w-4" /> Where to Improve ({teaser.opportunities_total} total)
                    </div>
                    {teaser.opportunities_preview?.map((o, i) => (
                      <div key={i} className="border-l-2 border-amber-500 pl-3 py-2 mb-2" data-testid={`opportunity-preview-${i}`}>
                        <div className="font-semibold text-slate-900 text-sm">{o.title}</div>
                        <div className="text-xs text-slate-600">{o.detail}</div>
                      </div>
                    ))}
                    <div className="text-xs text-slate-500 italic mt-1 flex items-center gap-1">
                      <Lock className="h-3 w-3" /> {teaser.opportunities_total - (teaser.opportunities_preview?.length || 0)} more in the full report
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Email opt-in */}
            {!emailed ? (
              <Card className="border-0 shadow-2xl bg-gradient-to-br from-emerald-50 to-white" data-testid="audit-widget-email-optin">
                <CardContent className="pt-6">
                  <div className="flex items-start gap-3">
                    <div className="bg-emerald-500 text-white p-2 rounded-full">
                      <Mail className="h-5 w-5" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-bold text-slate-900">Get the full PDF report</h3>
                      <p className="text-sm text-slate-600 mb-4">
                        We&apos;ll email the complete audit — all 5 strengths, 5 opportunities, and all 3 quick wins with concrete next steps.
                      </p>
                      <div className="grid gap-3 sm:grid-cols-3">
                        <div>
                          <Label htmlFor="prospect-email" className="text-slate-700 text-xs">Email <span className="text-red-500">*</span></Label>
                          <Input
                            id="prospect-email"
                            data-testid="audit-widget-email-input"
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            disabled={emailing}
                            placeholder="you@company.com"
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <Label htmlFor="prospect-name" className="text-slate-700 text-xs">Your Name</Label>
                          <Input
                            id="prospect-name"
                            data-testid="audit-widget-name-input"
                            type="text"
                            value={prospectName}
                            onChange={(e) => setProspectName(e.target.value)}
                            disabled={emailing}
                            placeholder="Jane Smith"
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <Label htmlFor="prospect-company" className="text-slate-700 text-xs">Company</Label>
                          <Input
                            id="prospect-company"
                            data-testid="audit-widget-company-input"
                            type="text"
                            value={prospectCompany}
                            onChange={(e) => setProspectCompany(e.target.value)}
                            disabled={emailing}
                            placeholder="Acme Inc."
                            className="mt-1"
                          />
                        </div>
                      </div>
                      <Button
                        onClick={submitEmail}
                        disabled={emailing}
                        data-testid="audit-widget-email-submit-btn"
                        className="mt-4 w-full sm:w-auto bg-emerald-600 hover:bg-emerald-700 text-white font-semibold"
                      >
                        {emailing ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Sending…</>) : (<><Mail className="h-4 w-4 mr-2" /> Email me the full report</>)}
                      </Button>
                      <p className="text-xs text-slate-500 mt-3">
                        We&apos;ll never share your email. {agency?.name || "Your partner"} may follow up with recommendations based on your results.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card className="border-0 shadow-2xl bg-emerald-50" data-testid="audit-widget-email-sent">
                <CardContent className="pt-6 text-center">
                  <CheckCircle2 className="h-10 w-10 text-emerald-600 mx-auto mb-3" />
                  <h3 className="text-lg font-bold text-slate-900">Report is on its way!</h3>
                  <p className="text-sm text-slate-600 mt-1">Check your inbox — the full PDF should arrive shortly.</p>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Footer */}
        <div className="text-center mt-10 text-xs text-slate-400">
          Powered by {agency?.name || "Ready Prospect"} · Instant AI-powered audit
        </div>
      </div>
    </div>
  );
}
